import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { stringify } from 'query-string';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher, fetchInfinite } from '@dashboard/services/request';
import {
  adminMerchantKey,
  adminMerchantBillingHistoryKey,
  adminSellersKey,
  adminUsersKey,
  adminPagesKey,
  adminPaymentsKey,
  adminStuckFreeChargeKey,
  adminBulkBookingImportsKey,
  adminSubmissionsKey,
  adminSubscriptionsKey,
  adminTemplatesKey,
  adminTemplateKey,
  adminTemplateCategoriesKey,
  adminTemplateCategoryKey,
  adminIntegrationLogsKey,
  adminStatisticLogsKey,
  adminMetricLogsKey,
  adminPlansKey,
  adminHelpCenterSearchIntentsKey,
  adminMerchantsUsageKey,
  adminPageCompareKey,
  adminPagePromptsKey,
  adminThemesKey,
  adminApiKeysKey,
  adminOAuthTokensKey,
  adminWebhooksKey,
  adminLinkInBiosKey,
  adminBrandLogKey,
  adminAiUsageSummaryKey,
  adminAiUsageStoresKey,
  adminAiUsageStoreKey,
  adminAiUsageTurnsKey,
  adminPageAiStatusKey,
} from '@dashboard/lib/queryKeys';

// Stale time constants as per PRD
const STALE_TIME_LIST = 30 * 1000; // 30 seconds for admin lists
const STALE_TIME_DETAIL = 60 * 1000; // 1 minute for admin details

async function fetchMerchant(token, sellerId) {
  return fetcher(`/admin/sellers/${sellerId}`, token);
}

export function useMerchant(sellerId, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminMerchantKey(sellerId),
    queryFn: () => fetchMerchant(token, sellerId),
    enabled: !!sellerId,
    staleTime: STALE_TIME_DETAIL,
    ...options,
  });
}

export function useMerchantBillingHistory(sellerId, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminMerchantBillingHistoryKey(sellerId),
    queryFn: () => fetcher(`/admin/sellers/${sellerId}/billing-history`, token),
    enabled: !!sellerId,
    staleTime: STALE_TIME_DETAIL,
    retry: false,
    ...options,
  });
}

async function fetchAdminSellers(token, filters, pageParam, limit) {
  const query = {
    ...filters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/sellers?${stringify(query)}`, token);
}

export function useAdminSellers(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminSellersKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminSellers(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

async function fetchAdminUsers(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/users?${stringify(query)}`, token);
}

export function useAdminUsers(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminUsersKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminUsers(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch paginated admin pages list
 */
async function fetchAdminPages(token, filters, pageParam, limit) {
  const {
    sortBy,
    sortDirection,
    search,
    pageType,
    usageType,
    hostedLayoutType,
    siteLayoutType,
    subscriptionStatus,
    hasOrderBumps,
    ...restFilters
  } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...(hasOrderBumps && { hasOrderBumps: 'true' }),
    ...(pageType && { type: pageType }),
    ...(usageType && { usageType }),
    ...(hostedLayoutType && { hostedLayoutType }),
    ...(siteLayoutType && { siteLayoutType }),
    ...(subscriptionStatus && { 'seller.subscriptionStatus': subscriptionStatus }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/pages?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin pages list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, search, pageType, usageType, etc.
 * @param {Object} options - Additional react-query options
 */
export function useAdminPages(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminPagesKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminPages(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch paginated admin payments list
 */
async function fetchAdminPayments(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/payments?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin payments list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, livemode, status, abandonmentEmailSent
 * @param {Object} options - Additional react-query options
 */
export function useAdminPayments(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminPaymentsKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminPayments(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

export function useStuckFreeCharge(chargeId, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminStuckFreeChargeKey(chargeId),
    queryFn: () => fetcher(`/admin/charges/${chargeId}/stuck-free`, token),
    enabled: !!chargeId,
    retry: false,
    ...options,
  });
}

export function useBulkBookingImports(options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminBulkBookingImportsKey(),
    queryFn: () => fetcher('/admin/bulk-booking-imports', token),
    refetchInterval: 5000,
    ...options,
  });
}

/**
 * Fetch paginated admin submissions list
 */
async function fetchAdminSubmissions(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/submissions?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin submissions list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, livemode, status
 * @param {Object} options - Additional react-query options
 */
export function useAdminSubmissions(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminSubmissionsKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminSubmissions(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch paginated admin subscriptions list
 */
async function fetchAdminSubscriptions(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/subscriptions?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin subscriptions list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, livemode, status, abandonmentEmailSent
 * @param {Object} options - Additional react-query options
 */
export function useAdminSubscriptions(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminSubscriptionsKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminSubscriptions(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch paginated admin templates list
 */
async function fetchAdminTemplates(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, category, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...(category && { category }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/templates?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin templates list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, search, category
 * @param {Object} options - Additional react-query options
 */
export function useAdminTemplates(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminTemplatesKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminTemplates(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch single template data by template ID
 */
async function fetchAdminTemplate(token, templateId) {
  return fetcher(`/templates/${templateId}`, token);
}

/**
 * Hook for fetching single template data
 * @param {string} templateId - The template ID to fetch
 * @param {Object} options - Additional react-query options
 */
export function useAdminTemplate(templateId, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminTemplateKey(templateId),
    queryFn: () => fetchAdminTemplate(token, templateId),
    enabled: !!templateId,
    staleTime: STALE_TIME_DETAIL,
    ...options,
  });
}

/**
 * Fetch paginated admin template categories list
 */
async function fetchAdminTemplateCategories(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/templates/categories?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin template categories list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, search
 * @param {Object} options - Additional react-query options
 */
export function useAdminTemplateCategories(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminTemplateCategoriesKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminTemplateCategories(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch single template category data by category ID
 */
async function fetchAdminTemplateCategory(token, categoryId) {
  return fetcher(`/templates/categories/${categoryId}`, token);
}

/**
 * Hook for fetching single template category data
 * @param {string} categoryId - The category ID to fetch
 * @param {Object} options - Additional react-query options
 */
export function useAdminTemplateCategory(categoryId, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminTemplateCategoryKey(categoryId),
    queryFn: () => fetchAdminTemplateCategory(token, categoryId),
    enabled: !!categoryId,
    staleTime: STALE_TIME_DETAIL,
    ...options,
  });
}

/**
 * Fetch all template categories (non-paginated, for dropdowns)
 */
async function fetchAdminTemplateCategoriesAll(token, filters = {}) {
  const { sortBy = 'order', sortDirection = 1 } = filters;
  const query = {
    sort: JSON.stringify({ [sortBy]: sortDirection }),
    limit: 99,
  };
  return fetcher(`/templates/categories?${stringify(query)}`, token);
}

/**
 * Hook for fetching all template categories (for dropdown use cases)
 * @param {Object} filters - Filter object containing sortBy, sortDirection
 * @param {Object} options - Additional react-query options
 */
export function useAdminTemplateCategoriesAll(filters = {}, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminTemplateCategoriesKey({ ...filters, all: true }),
    queryFn: () => fetchAdminTemplateCategoriesAll(token, filters),
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

// Stale time for static data (plans)
const STALE_TIME_STATIC = 5 * 60 * 1000; // 5 minutes for static data

/**
 * Fetch paginated admin integration logs list
 */
async function fetchAdminIntegrationLogs(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, status, event, integrationType, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(status && status !== 'all' ? { deliveryStatus: status } : {}),
    ...(event && event !== 'all' ? { event } : {}),
    ...(integrationType && integrationType !== 'all' ? { integrationType } : {}),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/integrations/logs?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin integration logs list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, status, event, integrationType
 * @param {Object} options - Additional react-query options
 */
export function useAdminIntegrationLogs(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminIntegrationLogsKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminIntegrationLogs(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch admin statistic logs list by status
 */
async function fetchAdminStatisticLogs(token, filters) {
  const { status, sortBy, sortDirection, limit = 5 } = filters;
  const query = {
    ...(status ? { status } : {}),
    ...(sortBy
      ? { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }
      : { sort: JSON.stringify({ _id: -1 }) }),
    limit,
  };
  return fetchInfinite(`/admin/statistics/logs?${stringify(query)}`, token);
}

/**
 * Hook for fetching admin statistic logs by status
 * @param {Object} filters - Filter object containing status (pending, processing, failed, completed), sortBy, sortDirection, limit
 * @param {Object} options - Additional react-query options
 */
export function useAdminStatisticLogs(filters = {}, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminStatisticLogsKey(filters),
    queryFn: () => fetchAdminStatisticLogs(token, filters),
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch admin metric logs list by status
 */
async function fetchAdminMetricLogs(token, filters) {
  const { status, sortBy, sortDirection, limit = 5 } = filters;
  const query = {
    ...(status ? { status } : {}),
    ...(sortBy
      ? { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }
      : { sort: JSON.stringify({ _id: -1 }) }),
    limit,
  };
  return fetchInfinite(`/admin/metrics/logs?${stringify(query)}`, token);
}

/**
 * Hook for fetching admin metric logs by status
 * @param {Object} filters - Filter object containing status (pending, processing, failed, completed), sortBy, sortDirection, limit
 * @param {Object} options - Additional react-query options
 */
export function useAdminMetricLogs(filters = {}, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminMetricLogsKey(filters),
    queryFn: () => fetchAdminMetricLogs(token, filters),
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch admin plans list
 */
async function fetchAdminPlans(token) {
  const query = {
    sort: JSON.stringify({ title: 1, version: -1 }),
    limit: 50,
  };
  return fetcher(`/admin/plans?${stringify(query)}`, token);
}

/**
 * Hook for fetching admin plans list
 * @param {Object} options - Additional react-query options
 */
export function useAdminPlans(options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminPlansKey(),
    queryFn: () => fetchAdminPlans(token),
    staleTime: STALE_TIME_STATIC,
    ...options,
  });
}

/**
 * Fetch paginated admin help center search intents list
 */
async function fetchAdminHelpCenterSearchIntents(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/help-center-search-intents?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin help center search intents list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, search
 * @param {Object} options - Additional react-query options
 */
export function useAdminHelpCenterSearchIntents(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminHelpCenterSearchIntentsKey(filters),
    queryFn: ({ pageParam = 0 }) =>
      fetchAdminHelpCenterSearchIntents(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch paginated admin merchants usage list
 */
async function fetchAdminMerchantsUsage(token, filters, pageParam, limit) {
  const {
    sortBy,
    sortDirection,
    search,
    plan,
    billingPeriod,
    subscriptionStatus,
    industry,
    hasCustomPricing,
    ...restFilters
  } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(plan && { 'plan.handle': plan }),
    ...(billingPeriod && { 'plan.billingPeriod': billingPeriod }),
    ...(subscriptionStatus && { subscriptionStatus }),
    ...(industry && { industry }),
    ...(hasCustomPricing === 'true' && {
      'plan.volumeLimit': JSON.stringify({ $exists: true, $ne: null }),
    }),
    ...(hasCustomPricing === 'false' && {
      $or: JSON.stringify([
        { 'plan.volumeLimit': { $exists: false } },
        { 'plan.volumeLimit': null },
      ]),
    }),
    ...(search && { search }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/merchants-usage?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin merchants usage list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, plan, billingPeriod, subscriptionStatus, industry, hasCustomPricing, search
 * @param {Object} options - Additional react-query options
 */
export function useAdminMerchantsUsage(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminMerchantsUsageKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminMerchantsUsage(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch single admin page for page compare feature
 */
export async function fetchAdminPageCompare(token, filters, skip = 0) {
  const { sortBy, sortDirection, search, pageType, usageType, hostedLayoutType, siteLayoutType } =
    filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...(pageType && { type: pageType }),
    ...(usageType && { usageType }),
    ...(hostedLayoutType && { hostedLayoutType }),
    ...(siteLayoutType && { siteLayoutType }),
    limit: 1,
    skip,
  };
  return fetchInfinite(`/admin/pages?${stringify(query)}`, token);
}

/**
 * Hook for fetching single page for page compare feature
 * Uses useInfiniteQuery with limit=1 for index-based navigation
 * @param {Object} filters - Filter object containing sortBy, sortDirection, search, pageType, usageType, etc.
 * @param {Object} options - Additional react-query options
 */
export function useAdminPageCompare(filters = {}, options = {}) {
  const { token } = useAuth();

  return useInfiniteQuery({
    queryKey: adminPageCompareKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminPageCompare(token, filters, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  });
}

/**
 * Fetch paginated admin page prompts list
 */
async function fetchAdminPagePrompts(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, promptType, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...(promptType && { type: promptType }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/page-prompts?${stringify(query)}`, token);
}

/**
 * Hook for fetching paginated admin page prompts list
 * @param {Object} filters - Filter object containing sortBy, sortDirection, search, promptType
 * @param {Object} options - Additional react-query options
 */
export function useAdminPagePrompts(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminPagePromptsKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminPagePrompts(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch paginated admin themes list
 */
async function fetchAdminThemes(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, scope, deprecated, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...(scope && { scope }),
    ...(deprecated && { deprecated }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/themes?${stringify(query)}`, token);
}

export function useAdminThemes(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminThemesKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminThemes(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch paginated admin API keys list
 */
async function fetchAdminApiKeys(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, permission, status, isOAuth, sellerId, ...restFilters } =
    filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...(permission && { permission }),
    ...(status && { status }),
    ...(isOAuth && { isOAuth }),
    ...(sellerId && { sellerId }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/api-keys?${stringify(query)}`, token);
}

export function useAdminApiKeys(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminApiKeysKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminApiKeys(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

export async function fetchAdminOAuthTokens(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, isRevoked, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...(typeof isRevoked === 'boolean' && { isRevoked }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/oauth-tokens?${stringify(query)}`, token);
}

export function useAdminOAuthTokens(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminOAuthTokensKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminOAuthTokens(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

export async function fetchAdminLinkInBios(token, filters, pageParam, limit) {
  const { search, sortBy, sortDirection } = filters;
  const query = {
    search: search || undefined,
    sortBy,
    sortDirection,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/link-in-bio?${stringify(query)}`, token);
}

export function useAdminLinkInBios(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminLinkInBiosKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminLinkInBios(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch paginated admin webhooks list
 */
async function fetchAdminWebhooks(token, filters, pageParam, limit) {
  const { sortBy, sortDirection, search, event, status, sellerId, ...restFilters } = filters;
  const query = {
    ...(sortBy && { sort: JSON.stringify({ [sortBy]: sortDirection || -1 }) }),
    ...(search && { search }),
    ...(event && { event }),
    ...(status && { status }),
    ...(sellerId && { sellerId }),
    ...restFilters,
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/webhooks?${stringify(query)}`, token);
}

export function useAdminWebhooks(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminWebhooksKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminWebhooks(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

/**
 * Fetch admin brand log
 */
async function fetchAdminBrandLog(token) {
  return fetcher('/admin/brand/log', token);
}

/**
 * Hook for fetching admin brand log
 * @param {Object} options - Additional react-query options
 */
export function useAdminBrandLog(options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminBrandLogKey(),
    queryFn: () => fetchAdminBrandLog(token),
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

const aiUsageQuery = query => stringify(query, { skipNull: true, skipEmptyString: true });

async function fetchAdminAiUsageSummary(token, { from, to }) {
  return fetcher(`/admin/ai-usage/summary?${aiUsageQuery({ from, to })}`, token);
}

export function useAdminAiUsageSummary(filters = {}, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminAiUsageSummaryKey(filters),
    queryFn: () => fetchAdminAiUsageSummary(token, filters),
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

export async function fetchAdminAiUsageStores(token, filters, pageParam, limit) {
  const { from, to, plan, search, sortBy, sortDirection } = filters;
  const query = { from, to, plan, search, sortBy, sortDirection, limit, skip: pageParam * limit };
  return fetchInfinite(`/admin/ai-usage/stores?${aiUsageQuery(query)}`, token);
}

export function useAdminAiUsageStores(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminAiUsageStoresKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminAiUsageStores(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

async function fetchAdminAiUsageStore(token, sellerId, { from, to }) {
  return fetcher(`/admin/ai-usage/stores/${sellerId}?${aiUsageQuery({ from, to })}`, token);
}

export function useAdminAiUsageStore(sellerId, filters = {}, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminAiUsageStoreKey(sellerId, filters),
    queryFn: () => fetchAdminAiUsageStore(token, sellerId, filters),
    enabled: !!sellerId,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}

export function useAdminPageAiStatus(pageId, options = {}) {
  const { token } = useAuth();

  return useQuery({
    queryKey: adminPageAiStatusKey(pageId),
    queryFn: () => fetcher(`/admin/ai-usage/pages/${pageId}`, token),
    enabled: !!pageId,
    ...options,
  });
}

export async function fetchAdminAiUsageTurns(token, filters, pageParam, limit) {
  const { from, to, seller, page, surface, status, offTopic } = filters;
  const query = {
    from,
    to,
    seller,
    page,
    surface,
    status,
    ...(offTopic && { offTopic: true }),
    limit,
    skip: pageParam * limit,
  };
  return fetchInfinite(`/admin/ai-usage/turns?${aiUsageQuery(query)}`, token);
}

export function useAdminAiUsageTurns(filters = {}, options = {}) {
  const { token } = useAuth();
  const limit = filters.limit || 20;

  return useInfiniteQuery({
    queryKey: adminAiUsageTurnsKey(filters),
    queryFn: ({ pageParam = 0 }) => fetchAdminAiUsageTurns(token, filters, pageParam, limit),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    staleTime: STALE_TIME_LIST,
    ...options,
  });
}
